import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CustomerService } from "../../services/CustomerService";
import Breadcrumb from "../common/Breadcrumb";

interface Company {
  id: number;
  name: string;
  apiKey: string;
  phone?: string;
  email?: string;
}

type SortField = 'name' | 'id';
type SortDirection = 'asc' | 'desc';

const Customers: React.FC = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [searchTerm, setSearchTerm] = useState<string>('');

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      const response = await CustomerService.customer.getCustomers();
      setCustomers(response.data);
    } catch (error) {
      console.error("Failed to load customers:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCustomerClick = (id: number) => {
    navigate(`/customers/${id}`);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };


  const filteredCustomers = customers.filter(customer => 
    customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.id.toString().includes(searchTerm) ||
    (customer.phone && customer.phone.includes(searchTerm)) ||
    (customer.email && customer.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const sortedCustomers = [...filteredCustomers].sort((a, b) => {
    const factor = sortDirection === 'asc' ? 1 : -1;
    
    if (sortField === 'name') {
      return a.name.localeCompare(b.name) * factor;
    } else if (sortField === 'id') {
      return (a.id - b.id) * factor;
    }
    
    return 0;
  });

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-4">

      <Breadcrumb items={[{ label: 'Customers' }]} />


      <div className="row mb-4 d-flex justify-content-between align-items-center">
        <div className="col-md-4 col-lg-3">
          <div className="input-group">
            <span className="input-group-text bg-light border-end-0">
              <i className="bi bi-search"></i>
            </span>
            <input
              type="text"
              className="form-control border-start-0"
              placeholder="Search customers..."
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
        </div>
      </div>

      <div className="container-fluid px-2 mb-4">
        <div className="card border-0 shadow-sm">
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="bg-light">
                  <tr>
                    <th 
                      scope="col" 
                      className="cursor-pointer" 
                      onClick={() => handleSort('id')}
                      style={{ cursor: "pointer" }}
                    >
                      ID
                      {sortField === 'id' && (
                        <i className={`bi bi-arrow-${sortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>
                      )}
                    </th>
                    <th 
                      scope="col" 
                      className="cursor-pointer" 
                      onClick={() => handleSort('name')}
                      style={{ cursor: "pointer" }}
                    >
                      Customer Name
                      {sortField === 'name' && (
                        <i className={`bi bi-arrow-${sortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>
                      )}
                    </th>
                    <th scope="col">Phone</th>
                    <th scope="col">Email</th>
                    <th scope="col" className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedCustomers.map((customer) => (
                    <tr 
                      key={customer.id} 
                      onClick={() => handleCustomerClick(customer.id)}
                      style={{ cursor: "pointer" }}
                    >
                      <td>{customer.id}</td>
                      <td className="fw-medium">{customer.name}</td>
                      <td>{customer.phone || '-'}</td>
                      <td>{customer.email || '-'}</td>
                      <td className="text-end">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCustomerClick(customer.id);
                          }}
                        >
                          <i className="bi bi-three-dots"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {sortedCustomers.length === 0 && (
              <div className="alert alert-info text-center m-3">
                No customers found matching your search.
              </div>
            )}
          </div>
        </div>
      </div>

    </div>
  );
};

export default Customers; 